import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import { createSaveStore } from '../../persistence/saveStore';
import {
  CLASS_LEVEL_CAP,
  GENERAL_LEVEL_CAP,
  createDefaultPermanentSave,
} from '../../persistence/schema';
import { paintInteriorBackdrop } from '../village/art';
import {
  addBackToVillageButton,
  addSecondaryButton,
  titleBlock,
} from '../ui/hubUi';

/**
 * Training Grounds foundation — show General/Class progression + Vanguard tree placeholder.
 * Full talent tree is explicitly out of scope for this checkpoint.
 */
export class TrainingGroundsScene extends Phaser.Scene {
  constructor() {
    super('TrainingGrounds');
  }

  create(): void {
    const cx = GAME_WIDTH / 2;
    const g = this.add.graphics();
    paintInteriorBackdrop(g, GAME_WIDTH, GAME_HEIGHT, 0x2a4034, 0x3d5a40);

    // Training posts
    const posts = this.add.graphics();
    posts.fillStyle(0x5a4030, 1);
    posts.fillRect(cx - 80, 160, 12, 100);
    posts.fillRect(cx + 68, 160, 12, 100);
    posts.fillStyle(0xc9a227, 1);
    posts.fillRect(cx - 80, 160, 160, 10);
    this.tweens.add({
      targets: posts,
      y: posts.y - 2,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    addBackToVillageButton(this);
    titleBlock(
      this,
      'Training Grounds',
      'Persistent progression. Open the Talents tab for the Vanguard tree.',
      300,
    );

    const store = createSaveStore();
    const permanent =
      store.load()?.permanent ?? createDefaultPermanentSave();
    const cls = permanent.classes.vanguard;
    const gen = permanent.general;
    const classUnspent = Math.max(0, cls.earnedPoints - cls.spentPoints);
    const generalUnspent = Math.max(0, gen.earnedPoints - gen.spentPoints);

    const lines = [
      `General  ·  Lv ${gen.level} / ${GENERAL_LEVEL_CAP}  ·  ${gen.xp} XP`,
      `General talents  ·  earned ${gen.earnedPoints}  ·  spent ${gen.spentPoints}  ·  unspent ${generalUnspent}`,
      `Vanguard ·  Lv ${cls.level} / ${CLASS_LEVEL_CAP}  ·  ${cls.xp} XP`,
      `Vanguard talents  ·  earned ${cls.earnedPoints}  ·  spent ${cls.spentPoints}  ·  unspent ${classUnspent}`,
      `Dice slots unlocked  ·  ${permanent.unlockedDiceSlots} / 6` +
        (permanent.specialDieSlotUnlocked ? ' · Special Die slot' : ''),
      `Milestone / achievement pts reserved (schedules OPEN)`,
    ];

    lines.forEach((line, i) => {
      const highlight =
        (i === 1 && generalUnspent > 0) || (i === 3 && classUnspent > 0);
      this.add
        .text(cx, 360 + i * 26, line, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: highlight ? '#c9a227' : '#d5d9e8',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 40 },
        })
        .setOrigin(0.5);
    });

    addSecondaryButton(
      this,
      cx,
      560,
      'Open Talents',
      () => {
        this.scene.start('Talents');
      },
      260,
    );

    this.add
      .text(
        cx,
        GAME_HEIGHT - 56,
        'Placeholder XP curves · talent combat bonuses are live',
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: '#6e7694',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 48 },
        },
      )
      .setOrigin(0.5);
  }
}
